"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

interface TOCItem {
  title: string;
  url: string;
  depth: number;
}

function useActiveItem(itemIds: string[]) {
  const [activeId, setActiveId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (itemIds.length > 0 && !activeId) {
      const firstId = itemIds[0];
      if (firstId) {
        setActiveId(firstId);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "0% 0% -80% 0%" },
    );

    for (const id of itemIds ?? []) {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      for (const id of itemIds ?? []) {
        const element = document.getElementById(id);
        if (element) {
          observer.unobserve(element);
        }
      }
    };
  }, [itemIds, activeId]);

  return activeId;
}

export function BlogTableOfContents({
  toc,
  className,
}: {
  toc: TOCItem[];
  className?: string;
}) {
  const itemIds = React.useMemo(
    () => toc.map((item) => item.url.replace("#", "")),
    [toc],
  );

  const activeHeading = useActiveItem(itemIds);

  const scrollToSection = (url: string) => {
    const id = url.replace("#", "");
    const element = document.getElementById(id);
    if (element) {
      const offset = 96;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  if (!toc?.length) {
    return null;
  }

  return (
    <div className={className}>
      <p className="text-marketing-text-secondary mb-4 font-mono text-xs tracking-[0.12em] uppercase dark:text-neutral-500">
        Contents
      </p>
      <nav className="border-marketing-border-light flex flex-col gap-2.5 border-l dark:border-white/10">
        {toc.map((item) => (
          <button
            key={item.url}
            onClick={() => {
              scrollToSection(item.url);
            }}
            className={cn(
              "-ml-px cursor-pointer border-l py-0.5 text-left text-sm leading-snug transition-colors",
              item.depth >= 4 ? "pl-10" : item.depth === 3 ? "pl-7" : "pl-4",
              item.url === `#${activeHeading}`
                ? "border-marketing-blue text-foreground dark:border-marketing-blue-light"
                : "text-marketing-text-muted hover:text-foreground border-transparent dark:text-neutral-400",
            )}
          >
            {item.title}
          </button>
        ))}
      </nav>
    </div>
  );
}
